/**
 * GeometryProblemBanner (F-5 b/c) — invalid geometry, said where the operator is looking.
 *
 * Before this, the ONLY signal that the print area + cutouts could not be published was the
 * `validation-status` line inside the rail's "Print area & cutouts" section — which is folded
 * shut by default. So the first thing an operator saw was a failed Publish, on a screen that
 * had been telling them nothing was wrong. That is the "don't break everything, show the
 * error" defect in one sentence.
 *
 * This is a page-level banner: it sits at the TOP of the masking step, above the canvas, and
 * it carries the fix rather than just the complaint. Design contract rule 9 — `.banner`,
 * danger tone, `role="alert"`, 720 px, one sentence.
 *
 * It renders NOTHING when the geometry is valid, and nothing on an empty canvas: a view with
 * no print area drawn yet is not a fault, and a red banner on a blank editor is how an
 * operator learns to ignore red banners.
 */
import { geometry } from "@genz/engine-bind";
import type { Mask, Polygon } from "@genz/engine-bind";
import { Icon } from "../ui/Icon.js";
import { geometryRepairMessage, shouldShowGeometryBanner } from "./geometryRepair.js";

export interface GeometryProblemBannerProps {
  printArea: Polygon;
  cutouts: Mask[];
  /** Apply the repair. The host owns the geometry, so the banner hands back BOTH halves. */
  onFix: (printArea: Polygon, cutouts: Mask[]) => void;
  /** Drop one cutout by index — the "Remove this cutout" shortcut. */
  onRemoveCutout?: (index: number) => void;
  /** Editing is off (a locked/Active version): show the problem, offer no fix. */
  locked?: boolean;
  /** Extra sentence from the host, e.g. why a Publish was refused. */
  detail?: string | null;
  /** Host layout hook (e.g. `tpl-editor__banner`, which places it in the editor's order
   *  bucket). Appended, never replacing `.banner banner--danger` — rule 9 owns the tone. */
  className?: string;
}

export function GeometryProblemBanner(p: GeometryProblemBannerProps) {
  const { printArea, cutouts, onFix, onRemoveCutout, locked, detail, className } = p;
  const validation = geometry.validate(printArea, cutouts);
  if (!shouldShowGeometryBanner(printArea, validation)) return null;

  const repair = geometry.repairGeometry(printArea, cutouts);
  const fixable = repair.unfixable.length === 0 && repair.changed;
  /* WS-ERR F-14 — "Remove this cutout" is offered for a DROPPED cutout only.
     This used to be "every cutout the repair did not return by identity", which also catches
     the CLIPPED ones — a cutout that merely spilled past the edge of the image and needs
     TRIMMING. Offering to delete that invites the operator to throw away a camera hole they
     wanted, in one click, from the banner that is meant to be the safe way out. With more
     than one at fault the shortcut says nothing at all: "this cutout" would not name one. */
  const offending = repair.droppedIndices;

  return (
    <div
      className={`banner banner--danger row--between row--wrap${className ? ` ${className}` : ""}`}
      role="alert"
      data-testid="geometry-banner"
    >
      <span>
        <Icon name="warning" size={16} />{" "}
        {geometry.geometryProblemMessage(validation.errors, { printArea, cutouts })}{" "}
        {fixable
          ? "Publish is blocked until it is fixed — one click below does it."
          : "Publish is blocked until it is fixed — redraw the print area on the canvas."}
        {detail ? ` ${detail}` : ""}
      </span>
      {!locked && (
        <span className="row row--tight">
          {fixable && (
            <button
              type="button"
              className="btn btn--sm"
              data-testid="geometry-fix"
              title={geometryRepairMessage(repair)}
              onClick={() => onFix(repair.printArea, repair.cutouts)}
            >
              Fix cutouts
            </button>
          )}
          {fixable && offending.length === 1 && onRemoveCutout && (
            <button
              type="button"
              className="btn btn--sm"
              data-testid="geometry-remove-cutout"
              onClick={() => onRemoveCutout(offending[0])}
            >
              Remove this cutout
            </button>
          )}
        </span>
      )}
    </div>
  );
}
